import { isEmpty } from 'lodash';

export const EmbeddableContextInjectionKey = Symbol('embeddableContext');

export const parseQueryFromEmbeddedView = (query) => {
  return Object.keys(query)
    .filter((key) =>
      [
        'breakdown',
        'geo',
        'geo_compare',
        'mode',
        'tour',
        'complexity',
      ].includes(key),
    )
    .reduce((acc, key) => ({ ...acc, [key]: query[key] }), {});
};

const getScriptInitTemplate = (
  indicator,
  tenant,
  url,
  wrapDOMContentLoadedEvent,
) => {
  const initWrapper = (body) =>
    `document.addEventListener("DOMContentLoaded", function(event) {
  ${body}
});`;

  const init = `datalas.init({tenant: '${tenant
    .toLocaleLowerCase()
    .trim()}',indicator: '${indicator.toLocaleLowerCase().trim()}'})`;

  return `
<script src="${url}/datalas/datalas.js"></script>
<script>
${wrapDOMContentLoadedEvent ? initWrapper(init) : init}
</script>`;
};

const getMultiEmbedScriptInitTemplate = (
  tenant,
  indicator,
  url,
  wrapDOMContentLoadedEvent,
) => {
  const initWrapper = (body) =>
    `document.addEventListener("DOMContentLoaded", function(event) {
  ${body}
});`;

  const init = `datalas.init({tenant: '${tenant
    .toLocaleLowerCase()
    .trim()}',indicator: '${indicator.toLocaleLowerCase().trim()}'})`;

  const aditionalCode = (initStatement) => `
const wrapper = document.querySelector('article');
wrapper.id = 'atlas-widget-container';
const innerEl = wrapper.firstElementChild;
const wrapper2 = document.createElement('atlas-embedded-view');
wrapper.appendChild(wrapper2);
wrapper2.appendChild(innerEl);
${initStatement}
`;

  return `
<script src="${url}/datalas/datalas.js"></script>
<script>
${
  wrapDOMContentLoadedEvent
    ? initWrapper(aditionalCode(init))
    : aditionalCode(init)
}
</script>`;
};

const getWidgetsTemplate = (components) => {
  const templates = [];

  if (!isEmpty(components)) {
    for (let index = 0; index < components.length; index += 1) {
      const component = components[index];
      const [componentName, componentOptions] = component;

      templates.push(
        `<${componentName} ${componentOptions.join(' ')}></${componentName}>`,
      );
    }

    return templates.join('\r\n');
  }

  return templates.join('');
};

// eslint-disable-next-line import/prefer-default-export
export const getEmbeddedTemplate = (
  indicator,
  tenant,
  components,
  url,
  wrapDOMContentLoadedEvent,
  skin,
) => {
  const styles = `<link type="text/css" rel="stylesheet" href="${url}/datalas/vAtlas.css" media="all">`;
  const container = `<div id="atlas-widget-container" class="${skin}-skin">`;
  const containerEnd = '</div>';

  const widgetsWrapper = '<atlas-embedded-view>';
  const widgetsWrapperEnd = '</atlas-embedded-view>';

  const widgets = getWidgetsTemplate(components);

  const markup = `
${container}
  ${widgetsWrapper}
    ${widgets}
  ${widgetsWrapperEnd}
${containerEnd}
`;

  const script = getScriptInitTemplate(
    indicator,
    tenant,
    url,
    wrapDOMContentLoadedEvent,
  );

  return `
${styles}
${markup}
${script}`;
};

const getMultiWidgetsTemplate = (components) => {
  const templates = {};

  if (!isEmpty(components)) {
    Object.entries(components).forEach((c) => {
      const [key, { componentTag, title, options }] = c;

      templates[key] = {
        title,
        embed: `<${componentTag}${options.length ? ' ' : ''}${options.join(
          ' ',
        )}></${componentTag}>`,
      };
    });

    return templates;
  }

  return null;
};

export const getMultiEmbedTemplate = (
  indicator,
  tenant,
  components,
  url,
  wrapDOMContentLoadedEvent,
) => {
  const styles = `<link type="text/css" rel="stylesheet" href="${url}/datalas/vAtlas.css" media="all">`;

  const widgets = getMultiWidgetsTemplate(components);
  const amountOfWidgets = Object.entries(widgets).length;

  const organizedWidgets = {
    firstEmbed: {},
    middleEmbeds: [],
    lastEmbed: {},
  };

  Object.entries(widgets).forEach((widget, i) => {
    const embedParts = [];
    const [key, { title, embed }] = widget;

    if (key === 'summary' || key === 'standalone') {
      embedParts.push(styles);
      if (widgets.standalone) embedParts.push(widgets.standalone?.embed);
      if (widgets.summary) embedParts.push(widgets.summary?.embed);

      const cleanTitle = (() =>
        `Embed code for Geo Selector ${
          widgets.summary ? 'and Overview Section' : ''
        }`)();

      organizedWidgets.firstEmbed = {
        title: cleanTitle,
        embed: embedParts.join('\r\n'),
      };
    } else if (i !== amountOfWidgets - 1) {
      const cleanTitle = (() =>
        title.charAt(title.length - 1) === '.'
          ? title.substring(0, title.length - 1)
          : title)();

      organizedWidgets.middleEmbeds.push({
        title: `Embed code for ${cleanTitle} Display`,
        embed,
      });
    } else if (i === amountOfWidgets - 1) {
      embedParts.push(embed);
      embedParts.push(
        getMultiEmbedScriptInitTemplate(
          indicator,
          tenant,
          url,
          wrapDOMContentLoadedEvent,
        ),
      );

      const cleanTitle = (() =>
        title.charAt(title.length - 1) === '.'
          ? title.substring(0, title.length - 1)
          : title)();

      organizedWidgets.lastEmbed = {
        title: `Embed code for ${cleanTitle} Display`,
        embed: embedParts.join('\r\n'),
      };
    }
  });

  return organizedWidgets;
};
